/**
 * Offline Storage Service for PWA
 * Saves photos and task updates locally when offline, syncs when back online
 */

const DB_NAME = 'mcb_offline_db';
const DB_VERSION = 1;
const PHOTOS_STORE = 'pending_photos';
const ACTIONS_STORE = 'pending_actions';

// Open IndexedDB
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Store for pending photos
      if (!db.objectStoreNames.contains(PHOTOS_STORE)) {
        const photosStore = db.createObjectStore(PHOTOS_STORE, { keyPath: 'id', autoIncrement: true });
        photosStore.createIndex('taskId', 'taskId', { unique: false });
      }
      
      // Store for pending actions (complete, start, etc.)
      if (!db.objectStoreNames.contains(ACTIONS_STORE)) {
        const actionsStore = db.createObjectStore(ACTIONS_STORE, { keyPath: 'id', autoIncrement: true });
        actionsStore.createIndex('taskId', 'taskId', { unique: false });
      }
    };
  });
};

// Check if we're online
export const isOnline = () => navigator.onLine;

// Save photo for later upload
export const savePendingPhoto = async (taskId, photoBlob, filename) => {
  try {
    const db = await openDB();
    const tx = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = tx.objectStore(PHOTOS_STORE);
    
    // Convert blob to base64 for storage
    const base64 = await blobToBase64(photoBlob);
    
    const photo = {
      taskId,
      filename,
      base64,
      type: photoBlob.type,
      createdAt: new Date().toISOString()
    };
    
    await new Promise((resolve, reject) => {
      const request = store.add(photo);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving pending photo:', error);
    return false;
  }
};

// Get pending photos for a task
export const getPendingPhotos = async (taskId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(PHOTOS_STORE, 'readonly');
    const store = tx.objectStore(PHOTOS_STORE);
    const index = store.index('taskId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(taskId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting pending photos:', error);
    return [];
  }
};

// Get all pending photos count
export const getPendingPhotosCount = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(PHOTOS_STORE, 'readonly');
    const store = tx.objectStore(PHOTOS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error counting pending photos:', error);
    return 0;
  }
};

// Delete a pending photo after successful upload
export const deletePendingPhoto = async (photoId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = tx.objectStore(PHOTOS_STORE);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(photoId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting pending photo:', error);
    return false;
  }
};

// Save pending action (complete task, start task, etc.)
export const savePendingAction = async (taskId, actionType, data = {}) => {
  try {
    const db = await openDB();
    const tx = db.transaction(ACTIONS_STORE, 'readwrite');
    const store = tx.objectStore(ACTIONS_STORE);
    
    const action = {
      taskId,
      actionType, // 'start', 'complete', 'end'
      data,
      createdAt: new Date().toISOString()
    };
    
    await new Promise((resolve, reject) => {
      const request = store.add(action);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving pending action:', error);
    return false;
  }
};

// Get all pending actions
export const getPendingActions = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(ACTIONS_STORE, 'readonly');
    const store = tx.objectStore(ACTIONS_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting pending actions:', error);
    return [];
  }
};

// Delete a pending action after successful sync
export const deletePendingAction = async (actionId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(ACTIONS_STORE, 'readwrite');
    const store = tx.objectStore(ACTIONS_STORE);
    
    await new Promise((resolve, reject) => {
      const request = store.delete(actionId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error deleting pending action:', error);
    return false;
  }
};

// Sync all pending items when back online
export const syncPendingItems = async (token, onProgress) => {
  if (!isOnline()) return { success: false, message: 'Still offline' };
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  let syncedPhotos = 0;
  let syncedActions = 0;
  let errors = [];
  
  try {
    // 1. Sync pending photos
    const db = await openDB();
    const photosTx = db.transaction(PHOTOS_STORE, 'readonly');
    const photosStore = photosTx.objectStore(PHOTOS_STORE);
    
    const photos = await new Promise((resolve, reject) => {
      const request = photosStore.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    for (const photo of photos) {
      try {
        // Convert base64 back to blob
        const blob = base64ToBlob(photo.base64, photo.type);
        const formData = new FormData();
        formData.append('file', blob, photo.filename);
        
        const response = await fetch(`${API_URL}/api/tasks/${photo.taskId}/photos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (response.ok) {
          await deletePendingPhoto(photo.id);
          syncedPhotos++;
          if (onProgress) onProgress({ type: 'photo', synced: syncedPhotos });
        } else {
          errors.push(`Photo upload failed: ${response.statusText}`);
        }
      } catch (error) {
        errors.push(`Photo sync error: ${error.message}`);
      }
    }
    
    // 2. Sync pending actions
    const actions = await getPendingActions();
    
    for (const action of actions) {
      try {
        let endpoint = '';
        let method = 'POST';
        let body = null;
        
        switch (action.actionType) {
          case 'start':
            endpoint = `/api/tasks/${action.taskId}/start`;
            break;
          case 'end':
            endpoint = `/api/tasks/${action.taskId}/end`;
            break;
          case 'complete':
            endpoint = `/api/tasks/${action.taskId}/complete`;
            break;
          default:
            continue;
        }
        
        const response = await fetch(`${API_URL}${endpoint}`, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: body ? JSON.stringify(body) : null
        });
        
        if (response.ok) {
          await deletePendingAction(action.id);
          syncedActions++;
          if (onProgress) onProgress({ type: 'action', synced: syncedActions });
        } else {
          const errorData = await response.json().catch(() => ({}));
          // Don't treat "already done" as error
          if (errorData.detail?.includes('déjà') || errorData.detail?.includes('already')) {
            await deletePendingAction(action.id);
          } else {
            errors.push(`Action ${action.actionType} failed: ${errorData.detail || response.statusText}`);
          }
        }
      } catch (error) {
        errors.push(`Action sync error: ${error.message}`);
      }
    }
    
    return {
      success: errors.length === 0,
      syncedPhotos,
      syncedActions,
      errors
    };
  } catch (error) {
    return { success: false, message: error.message };
  }
};

// Helper: Convert Blob to Base64
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper: Convert Base64 to Blob
const base64ToBlob = (base64, type) => {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type });
};

// Check if there are pending items to sync
export const hasPendingItems = async () => {
  const photosCount = await getPendingPhotosCount();
  const actions = await getPendingActions();
  return photosCount > 0 || actions.length > 0;
};

export default {
  isOnline,
  savePendingPhoto,
  getPendingPhotos,
  getPendingPhotosCount,
  deletePendingPhoto,
  savePendingAction,
  getPendingActions,
  deletePendingAction,
  syncPendingItems,
  hasPendingItems
};
